import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { format, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Bell } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { useGetPushLogs } from '~entities/mobile-inventor-push/api/get-push-logs';
import type { OutgoingPushLogItem } from '~entities/mobile-inventor-push/model/types';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import {
  formatPushModeLabel,
  formatPushScenarioLabel,
  formatPushStatusLabel,
  PUSH_SCENARIO_OPTIONS,
  PUSH_STATUS_OPTIONS,
  pushStatusBadgeVariant,
} from '../utils/push-notification-labels';

const PAGE_SIZE = 20;
const columnHelper = createColumnHelper<OutgoingPushLogItem>();

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

/** Safe zoned format — one bad timestamp must not crash the history table. */
function formatPushTimestamp(
  value: string | null | undefined,
  timezone: string,
  fmt: string,
): string {
  if (value == null || value === '') return '—';
  try {
    const date = new Date(value);
    if (!isValid(date)) return '—';
    return formatInTimeZone(date, timezone, fmt);
  } catch {
    return '—';
  }
}

function formatDeliverySummary(log: OutgoingPushLogItem): string {
  if (log.status === 'handoff') {
    return `${log.matchedEmails} parent(s) staged`;
  }
  if (log.status === 'skipped') {
    return 'Not sent';
  }
  return `${log.matchedEmails} parent(s) · ${log.recipientCount} device(s)`;
}

export const PushSendHistory = () => {
  const studioTimezone =
    useCurrentStudio()?.timezone.trim() ?? 'America/New_York';
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [scenarioFilter, setScenarioFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [previewLog, setPreviewLog] = useState<OutgoingPushLogItem | null>(
    null,
  );

  const startDate = useMemo(() => {
    if (!dateRange?.from) return undefined;
    // Civil day from the date picker. Backend expands this YMD across the
    // studio timezone so the filter matches the Sent column.
    return format(dateRange.from, 'yyyy-MM-dd');
  }, [dateRange?.from]);

  const endDate = useMemo(() => {
    if (!dateRange?.to) return undefined;
    return format(dateRange.to, 'yyyy-MM-dd');
  }, [dateRange?.to]);

  const { data, isLoading, isError } = useGetPushLogs({
    page,
    limit: PAGE_SIZE,
    scenario: scenarioFilter !== 'all' ? scenarioFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    startDate,
    endDate,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('pushScenario', {
        header: 'Type',
        cell: ({ row }) => (
          <div>
            <div className='font-medium'>
              {formatPushScenarioLabel(row.original.pushScenario)}
            </div>
            <p className='text-xs text-muted-foreground'>
              {formatPushModeLabel(row.original.mode)}
            </p>
          </div>
        ),
      }),
      columnHelper.accessor('notificationText', {
        header: 'Message',
        cell: ({ row }) => (
          <div>
            <div className='line-clamp-2 font-medium'>
              {row.original.title ?? row.original.notificationText ?? '—'}
            </div>
            {row.original.title && row.original.notificationText ? (
              <div className='line-clamp-2 text-xs text-muted-foreground'>
                {row.original.notificationText}
              </div>
            ) : null}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'delivery',
        header: 'Delivery',
        cell: ({ row }) => (
          <div>
            {formatDeliverySummary(row.original)}
            {row.original.unmatchedEmails.length > 0 ? (
              <p className='text-xs text-amber-700'>
                {row.original.unmatchedEmails.length} unmatched
              </p>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => (
          <Badge variant={pushStatusBadgeVariant(info.getValue())}>
            {formatPushStatusLabel(info.getValue())}
          </Badge>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Sent at',
        cell: ({ row }) => (
          <div>
            {formatPushTimestamp(
              row.original.createdAt,
              studioTimezone,
              'MMM d, yyyy HH:mm zzz',
            )}
            {row.original.scheduledFor ? (
              <p className='text-xs'>
                Scheduled:{' '}
                {formatPushTimestamp(
                  row.original.scheduledFor,
                  studioTimezone,
                  'MMM d, yyyy HH:mm zzz',
                )}
              </p>
            ) : null}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Details',
        cell: ({ row }) => (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => {
              setPreviewLog(row.original);
            }}
          >
            View
          </Button>
        ),
      }),
    ],
    [studioTimezone],
  );
  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: PAGE_SIZE,
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className='flex items-start gap-3'>
            <Bell className='mt-0.5 size-5 shrink-0 text-muted-foreground' />
            <div className='min-w-0 space-y-2'>
              <CardTitle>Send history</CardTitle>
              <CardDescription className='max-w-3xl'>
                Review push notifications sent from the portal. Delivery counts
                show how many parents were matched to the app and how many
                devices received the notification.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='py-8 text-center text-muted-foreground'>
              Loading…
            </div>
          ) : null}
          {isError ? (
            <div className='py-8 text-center text-destructive'>
              Failed to load push history. Please try again.
            </div>
          ) : null}
          {!isError ? (
            <EnrollioTable
              totalLabel='Push notifications'
              filterHeader={(table) => (
                <>
                  <EnrollioTableSearch
                    table={table}
                    placeholder='Search notifications...'
                  />
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Date range
                    </p>
                    <DatePickerWithRange
                      date={dateRange}
                      setDate={(range) => {
                        setDateRange(range);
                        setPage(1);
                      }}
                    />
                  </div>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Notification type
                    </p>
                    <Select
                      value={scenarioFilter}
                      onValueChange={(value) => {
                        setScenarioFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className='w-full max-w-full'>
                        <SelectValue placeholder='All types' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All types</SelectItem>
                        {PUSH_SCENARIO_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='min-w-0 space-y-1.5'>
                    <p className='text-xs font-medium text-muted-foreground'>
                      Status
                    </p>
                    <Select
                      value={statusFilter}
                      onValueChange={(value) => {
                        setStatusFilter(value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className='w-full max-w-full'>
                        <SelectValue placeholder='All statuses' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All statuses</SelectItem>
                        {PUSH_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              data={data?.data ?? []}
              columns={columns}
              rowCount={data?.total ?? 0}
              manualPagination
              pagination={pagination}
              onPaginationChange={(updater) => {
                const next =
                  typeof updater === 'function' ? updater(pagination) : updater;
                setPage(next.pageIndex + 1);
              }}
              getRowId={(row) =>
                row._id ?? `${row.createdAt}-${row.pushScenario}`
              }
              isUpdating={isLoading}
              emptyContent='No push notifications match your filters.'
              ariaLabel='Push notification history'
            />
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={previewLog != null}
        onOpenChange={(open) => {
          if (!open) setPreviewLog(null);
        }}
      >
        <DialogContent className='max-h-[90vh] max-w-3xl overflow-auto'>
          <DialogHeader>
            <DialogTitle>Push notification details</DialogTitle>
          </DialogHeader>
          {previewLog ? (
            <div className='space-y-4'>
              <dl className='grid grid-cols-[minmax(0,auto)_1fr] gap-x-4 gap-y-2 text-sm'>
                <dt className='font-medium text-muted-foreground'>Type</dt>
                <dd>{formatPushScenarioLabel(previewLog.pushScenario)}</dd>
                <dt className='font-medium text-muted-foreground'>Status</dt>
                <dd>
                  <Badge variant={pushStatusBadgeVariant(previewLog.status)}>
                    {formatPushStatusLabel(previewLog.status)}
                  </Badge>
                </dd>
                <dt className='font-medium text-muted-foreground'>Mode</dt>
                <dd>{formatPushModeLabel(previewLog.mode)}</dd>
                <dt className='font-medium text-muted-foreground'>Sent</dt>
                <dd>
                  {formatPushTimestamp(
                    previewLog.createdAt,
                    studioTimezone,
                    'MMM d, yyyy HH:mm:ss zzz',
                  )}
                </dd>
                {previewLog.scheduledFor ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>
                      Scheduled for
                    </dt>
                    <dd>
                      {formatPushTimestamp(
                        previewLog.scheduledFor,
                        studioTimezone,
                        'MMM d, yyyy HH:mm:ss zzz',
                      )}
                    </dd>
                  </>
                ) : null}
                <dt className='font-medium text-muted-foreground'>
                  Parents matched
                </dt>
                <dd>{previewLog.matchedEmails}</dd>
                <dt className='font-medium text-muted-foreground'>
                  Devices reached
                </dt>
                <dd>{previewLog.recipientCount}</dd>
                {previewLog.unmatchedEmails.length > 0 ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>
                      Unmatched emails
                    </dt>
                    <dd className='break-all text-muted-foreground'>
                      {previewLog.unmatchedEmails.join(', ')}
                    </dd>
                  </>
                ) : null}
                {previewLog.errorMessage ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>Error</dt>
                    <dd className='break-words text-destructive'>
                      {previewLog.errorMessage}
                    </dd>
                  </>
                ) : null}
                {previewLog.title ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>Title</dt>
                    <dd className='break-words'>{previewLog.title}</dd>
                  </>
                ) : null}
                {previewLog.notificationText ? (
                  <>
                    <dt className='font-medium text-muted-foreground'>
                      Notification text
                    </dt>
                    <dd className='break-words'>
                      {previewLog.notificationText}
                    </dd>
                  </>
                ) : null}
              </dl>
              {previewLog.detail ? (
                <div>
                  <p className='mb-1 text-xs font-medium text-muted-foreground'>
                    Detail
                  </p>
                  {/*
                    Sandboxed iframe matches outgoing email logs — avoids
                    DOMPurify ALLOW_UNKNOWN_PROTOCOLS / a+img href+src XSS
                    when rendering stored push HTML in the parent document.
                  */}
                  <iframe
                    title='Push detail preview'
                    className='h-[min(240px,40vh)] w-full rounded-md border bg-white'
                    sandbox=''
                    srcDoc={previewLog.detail}
                  />
                  <pre className='mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground'>
                    {stripHtmlTags(previewLog.detail)}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
